import React, { createContext, useContext, useEffect, useState } from 'react';
import { 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged 
} from 'firebase/auth';
import { auth } from '../config/firebase';
import { createUserProfile, getUserProfile } from '../config/supabaseClient';
import { useToast } from '../hooks/use-toast';
import UserProfileService from '../services/userProfileService';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  // Fonction d'inscription
  const signup = async (email, password, username) => {
    try {
      setLoading(true);
      
      console.log('🔥 Début inscription:', { email, username });
      
      // ÉTAPE 1: Créer le compte Firebase
      const { user: firebaseUser } = await createUserWithEmailAndPassword(auth, email, password);
      console.log('✅ Compte Firebase créé:', firebaseUser.uid);
      
      // ÉTAPE 2: Créer le profil Supabase via l'API backend (plus robuste)
      try {
        console.log('🗄️ Création profil Supabase via API backend...');
        const supabaseProfile = await UserProfileService.createUserProfileIfNotExists(
          firebaseUser.uid, 
          {
            email: firebaseUser.email,
            username: username,
            role: 'viewer'
          }
        );
        console.log('✅ Profil Supabase créé:', supabaseProfile);
        
        toast({
          title: "Inscription réussie !",
          description: `Bienvenue ${username} sur YUUME - Profil complet créé`,
        });
        
      } catch (supabaseError) {
        console.error('❌ Erreur création profil Supabase:', supabaseError);
        
        // Afficher un avertissement mais ne pas bloquer l'inscription
        toast({
          title: "Inscription partiellement réussie",
          description: "Compte Firebase créé, mais erreur profil Supabase. Contactez le support.",
          variant: "destructive"
        });
      }

      return { user: firebaseUser, error: null };
    } catch (error) {
      console.error('Erreur inscription:', error);
      let errorMessage = "Erreur d'inscription";
      let errorTitle = "Erreur d'inscription";
      
      if (error.code === 'auth/email-already-in-use') {
        errorTitle = "Compte déjà existant";
        errorMessage = "Cet email est déjà utilisé. Voulez-vous vous connecter à la place ?";
      } else if (error.code === 'auth/weak-password') {
        errorMessage = "Le mot de passe doit contenir au moins 6 caractères";
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = "Format d'email invalide";
      } else if (error.code === 'auth/operation-not-allowed') {
        errorMessage = "L'inscription par email n'est pas activée";
      } else {
        errorMessage = error.message || "Une erreur inattendue s'est produite";
      }

      toast({
        title: errorTitle,
        description: errorMessage,
        variant: "destructive",
      });
      return { user: null, error: { code: error.code, message: errorMessage } };
    } finally {
      setLoading(false);
    }
  };

  // Fonction de connexion
  const signin = async (email, password) => {
    try {
      setLoading(true);
      const { user: firebaseUser } = await signInWithEmailAndPassword(auth, email, password);
      
      toast({
        title: "Connexion réussie !",
        description: "Bon retour sur YUUME",
      });

      return { user: firebaseUser, error: null };
    } catch (error) {
      console.error('Erreur connexion Firebase:', error.code, error.message);
      let errorMessage = "Erreur de connexion";
      
      if (error.code === 'auth/user-not-found') {
        errorMessage = "Aucun utilisateur trouvé avec cet email";
      } else if (error.code === 'auth/wrong-password') {
        errorMessage = "Mot de passe incorrect";
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = "Email invalide";
      } else if (error.code === 'auth/invalid-credential') {
        errorMessage = "Email ou mot de passe incorrect";
      } else if (error.code === 'auth/too-many-requests') {
        errorMessage = "Trop de tentatives. Réessayez plus tard ou réinitialisez votre mot de passe.";
      } else if (error.code === 'auth/network-request-failed') {
        errorMessage = "Erreur réseau. Vérifiez votre connexion internet.";
      } else if (error.code === 'auth/user-disabled') {
        errorMessage = "Ce compte a été désactivé.";
      } else {
        errorMessage = error.message || "Une erreur inattendue s'est produite";
      }

      toast({
        title: "Erreur de connexion",
        description: errorMessage,
        variant: "destructive",
      });

      return { user: null, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  // Fonction de déconnexion
  const logout = async () => {
    try {
      await signOut(auth);
      setUser(null);
      setUserProfile(null);
      
      toast({
        title: "Déconnexion réussie",
        description: "À bientôt sur YUUME",
      });
    } catch (error) {
      console.error('Erreur déconnexion:', error);
      toast({
        title: "Erreur de déconnexion",
        description: "Une erreur est survenue",
        variant: "destructive",
      });
    }
  };

  // Écouter les changements d'état d'authentification
  useEffect(() => {
    let isMounted = true;
    
    const initializeAuth = () => {
      // Timeout de sécurité - si Firebase ne répond pas dans 5 secondes
      const timeoutId = setTimeout(() => {
        if (isMounted) {
          console.warn('Timeout Firebase auth - initialisation forcée sans utilisateur');
          setUser(null);
          setUserProfile(null);
          setLoading(false);
        }
      }, 5000);

      const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
        try {
          // Effacer le timeout puisque Firebase répond
          clearTimeout(timeoutId);
          
          if (!isMounted) return; // Éviter les updates si le composant est démonté

          if (firebaseUser) {
            setUser(firebaseUser);
            
            // Essayer de charger le profil Supabase, le créer s'il n'existe pas
            try {
              const { data: profile } = await getUserProfile(firebaseUser.uid);
              
              if (profile) {
                // Profil existe, l'utiliser
                if (isMounted) {
                  setUserProfile(profile);
                }
              } else {
                // Profil n'existe pas, le créer
                console.log('Création du profil Supabase pour:', firebaseUser.email);
                try {
                  const createResponse = await UserProfileService.createUserProfileIfNotExists(firebaseUser.uid, {
                    email: firebaseUser.email || '',
                    username: firebaseUser.displayName || firebaseUser.email?.split('@')[0] || '',
                    role: 'viewer'
                  });
                  
                  if (createResponse.success && createResponse.data) {
                    if (isMounted) {
                      setUserProfile(createResponse.data);
                    }
                  } else {
                    // Fallback avec données Firebase
                    if (isMounted) {
                      setUserProfile({
                        id: firebaseUser.uid,
                        email: firebaseUser.email,
                        username: firebaseUser.displayName || '',
                        role: 'viewer',
                        avatar_url: firebaseUser.photoURL || ''
                      });
                    }
                  }
                } catch (createError) {
                  console.error('Erreur création profil Supabase:', createError);
                  if (isMounted) {
                    setUserProfile({
                      id: firebaseUser.uid,
                      email: firebaseUser.email,
                      username: firebaseUser.displayName || '',
                      role: 'viewer',
                      avatar_url: firebaseUser.photoURL || ''
                    });
                  }
                }
              }
            } catch (profileError) {
              console.warn('Profil Supabase indisponible:', profileError);
              if (isMounted) {
                setUserProfile({
                  id: firebaseUser.uid,
                  email: firebaseUser.email,
                  username: firebaseUser.displayName || '',
                  role: 'viewer',
                  avatar_url: firebaseUser.photoURL || ''
                });
              }
            }
          } else {
            if (isMounted) {
              setUser(null);
              setUserProfile(null);
            }
          }
        } catch (error) {
          console.error('Erreur dans onAuthStateChanged:', error);
          if (isMounted) {
            setUser(null);
            setUserProfile(null);
          }
        } finally {
          if (isMounted) {
            setLoading(false);
          }
        }
      });

      return () => {
        clearTimeout(timeoutId);
        unsubscribe();
      };
    };

    const cleanup = initializeAuth();

    return () => {
      isMounted = false;
      if (cleanup) cleanup();
    };
  }, []);

  // Fonction pour rafraîchir le profil utilisateur
  const refreshUserProfile = async () => {
    if (!user) return;
    
    try {
      // Utiliser notre API backend au lieu de l'accès direct Supabase
      const response = await UserProfileService.getUserProfile(user.uid);
      if (response.success) {
        setUserProfile(response.data);
      } else {
        // Fallback vers la méthode directe Supabase
        const { data: profile } = await getUserProfile(user.uid);
        setUserProfile(profile);
      }
    } catch (profileError) {
      console.warn('Impossible de rafraîchir le profil via API, essai direct Supabase:', profileError);
      try {
        const { data: profile } = await getUserProfile(user.uid);
        setUserProfile(profile);
      } catch (fallbackError) {
        console.warn('Impossible de rafraîchir le profil:', fallbackError);
      }
    }
  };

  const value = {
    user,
    userProfile,
    loading,
    signup,
    signin,
    logout,
    refreshUserProfile
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};